import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, Filter, MoreVertical, Building2, Shield,
  UserCheck, UserX, Key, Eye, RefreshCw, Loader2,
  ChevronLeft, ChevronRight, AlertCircle, LogOut, X, Info, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';

/**
 * SystemUsersPage - Platform-level User Directory
 * 
 * This page shows ALL users across ALL tenants. This is distinct from
 * tenant-level user management which only shows users within a single tenant.
 * 
 * Platform Admin = Global view across tenants (this page)
 * Tenant Admin = Scoped view within a single tenant (/admin/users)
 */

// Role display mapping
const ROLE_LABELS = {
  sysadmin: { label: 'System Admin', color: 'bg-red-100 text-red-700' },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  pmo_power: { label: 'PMO Power', color: 'bg-blue-100 text-blue-700' },
  pmo_standard: { label: 'PMO Standard', color: 'bg-blue-50 text-blue-600' },
  pm: { label: 'Project Manager', color: 'bg-green-100 text-green-700' },
  executive: { label: 'Executive', color: 'bg-amber-100 text-amber-700' },
  viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-600' },
  consultant: { label: 'Consultant', color: 'bg-indigo-100 text-indigo-700' }
};

const SystemUsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showActionsFor, setShowActionsFor] = useState(null);
  
  // Impersonation state
  const [impersonating, setImpersonating] = useState(null);
  
  const pageSize = 25;

  useEffect(() => {
    loadTenants();
    checkImpersonation();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [page, tenantFilter, roleFilter, statusFilter]);

  const checkImpersonation = () => {
    const impersonatedId = localStorage.getItem('impersonated_tenant_id');
    const impersonatedName = localStorage.getItem('impersonated_tenant_name');
    if (impersonatedId) {
      setImpersonating({ id: impersonatedId, name: impersonatedName });
    }
  };

  const loadTenants = async () => {
    try {
      const response = await api.listTenants({ limit: 500 });
      setTenants(response.data.tenants || []);
    } catch (err) {
      console.error('Failed to load tenants:', err);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize
      };
      
      if (tenantFilter) params.tenant_id = tenantFilter;
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await api.getPlatformUsers(params);
      setUsers(response.data.users || []);
      setTotalCount(response.data.total || 0);
    } catch (err) {
      console.error('Failed to load users:', err);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(() => {
    setPage(1);
    loadUsers();
  }, [searchQuery]);

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.tenant_name?.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  const handleImpersonate = async (user) => {
    setActionLoading(user.id);
    try {
      await api.impersonateUser(user.id);
      
      // Store impersonation context
      localStorage.setItem('impersonated_tenant_id', user.tenant_id);
      localStorage.setItem('impersonated_tenant_name', user.tenant_name || 'Unknown');
      localStorage.setItem('impersonated_user_id', user.id);
      localStorage.setItem('impersonated_user_email', user.email);
      
      setImpersonating({
        id: user.tenant_id,
        name: user.tenant_name,
        userId: user.id,
        userEmail: user.email
      });
      
      toast.success(`Impersonating ${user.email}`);
      setShowActionsFor(null);
      
      // Navigate to dashboard
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to start impersonation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleExitImpersonation = () => {
    localStorage.removeItem('impersonated_tenant_id');
    localStorage.removeItem('impersonated_tenant_name');
    localStorage.removeItem('impersonated_user_id');
    localStorage.removeItem('impersonated_user_email');
    setImpersonating(null);
    toast.success('Exited impersonation mode');
  };

  const handleSuspend = async (user) => {
    if (!window.confirm(`Suspend user "${user.email}"? They will no longer be able to sign in.`)) {
      return;
    }
    
    setActionLoading(user.id);
    try {
      await api.suspendPlatformUser(user.id);
      toast.success(`User ${user.email} suspended`);
      loadUsers();
    } catch (err) {
      toast.error('Failed to suspend user');
    } finally {
      setActionLoading(null);
      setShowActionsFor(null);
    }
  };

  const handleActivate = async (user) => {
    setActionLoading(user.id);
    try {
      await api.activatePlatformUser(user.id);
      toast.success(`User ${user.email} activated`);
      loadUsers();
    } catch (err) {
      toast.error('Failed to activate user');
    } finally {
      setActionLoading(null);
      setShowActionsFor(null);
    }
  };

  const handleResendInvite = async (user) => {
    setActionLoading(user.id);
    try {
      const response = await api.resendUserInvite(user.id);
      const data = response.data;
      
      // Show credentials in a toast since email is not configured
      toast.success(
        <div className="space-y-2">
          <p className="font-medium text-green-800">✅ Password reset — credentials below</p>
          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <p><strong>Email:</strong> {data.credentials.email}</p>
            <p><strong>Temp Password:</strong> <code className="bg-white px-2 py-0.5 rounded border">{data.credentials.temp_password}</code></p>
            <p><strong>Login URL:</strong> <a href={data.credentials.login_url} className="text-blue-600 underline">{data.credentials.login_url}</a></p>
          </div>
          <p className="text-xs text-gray-600">User must change password on first login.</p>
        </div>,
        { duration: 30000, className: 'w-96' }
      );
      
      loadUsers();
    } catch (err) {
      toast.error('Failed to reset credentials');
    } finally {
      setActionLoading(null);
      setShowActionsFor(null);
    }
  };

  const getRoleBadge = (role) => {
    const config = ROLE_LABELS[role] || { label: role, color: 'bg-gray-100 text-gray-600' };
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <TooltipProvider>
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Platform Admin Context Banner */}
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Shield className="w-5 h-5 text-slate-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-slate-700">Platform Admin - Cross-Tenant User Directory</p>
            <p className="text-xs text-slate-500">
              This view shows <strong>all users across all tenants</strong>. 
              For tenant-specific user management, navigate to Admin → Tenant Configuration within the tenant context.
            </p>
          </div>
        </div>
        
        {/* Impersonation Banner */}
        {impersonating && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Eye className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-amber-900">Impersonation Mode Active</p>
                <p className="text-sm text-amber-700">
                  Viewing as: {impersonating.name || 'Unknown Tenant'}
                  {impersonating.userEmail && ` (${impersonating.userEmail})`}
                </p>
              </div>
            </div>
            <button
              onClick={handleExitImpersonation}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              data-testid="exit-impersonation-btn"
            >
              <LogOut className="w-4 h-4" />
              Exit Impersonation
            </button>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Users</h1>
              <p className="text-sm text-gray-500">Manage users across all tenants</p>
            </div>
          </div>
        </div>
        
        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                data-testid="user-search-input"
              />
            </div>
            
            {/* Tenant Filter */}
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <select
                value={tenantFilter}
                onChange={(e) => { setTenantFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="tenant-filter"
              >
                <option value="">All Tenants</option>
                {tenants.map(t => (
                  <option key={t.id || t._id} value={t.id || t._id}>{t.name}</option>
                ))}
              </select>
            </div>
            
            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-400" />
              <select
                value={roleFilter}
                onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="role-filter"
              >
                <option value="">All Roles</option>
                {Object.entries(ROLE_LABELS).map(([key, config]) => (
                  <option key={key} value={key}>{config.label}</option>
                ))}
              </select>
            </div>
            
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                data-testid="status-filter"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            {/* Refresh */}
            <button
              onClick={loadUsers}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              data-testid="refresh-users-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100">
            <div className="text-sm">
              <span className="text-gray-500">Total Users:</span>
              <span className="ml-1 font-semibold text-gray-900">{totalCount}</span>
            </div>
          </div>
        </div>
        
        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-visible">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No users found</p>
              {(searchQuery || tenantFilter || roleFilter || statusFilter) && (
                <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
              )}
            </div>
          ) : (
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Tenant</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Last Login</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user, index) => (
                  <tr 
                    key={user.id} 
                    className="hover:bg-gray-50 transition-colors"
                    data-testid={`user-row-${user.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-indigo-600">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{user.name || 'No Name'}</p>
                          <p className="text-xs text-gray-500">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{user.tenant_name || 'No Tenant'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getRoleBadge(user.primary_role)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.is_active !== false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {user.last_login 
                          ? new Date(user.last_login).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1 relative">
                        {/* Impersonate button - disabled with tooltip explaining limitation */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              disabled
                              className="p-1.5 text-gray-300 cursor-not-allowed rounded"
                              data-testid={`impersonate-${user.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-xs">Impersonation not yet available. Use "Enter Tenant Context" from Tenant Directory instead.</p>
                          </TooltipContent>
                        </Tooltip>
                        
                        <button
                          onClick={() => setShowActionsFor(showActionsFor === user.id ? null : user.id)}
                          className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                          data-testid={`user-actions-${user.id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {showActionsFor === user.id && (
                          <div 
                            className="absolute right-0 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                            style={{
                              // Position dropdown above if near bottom of viewport
                              bottom: index >= filteredUsers.length - 2 ? '100%' : 'auto',
                              top: index >= filteredUsers.length - 2 ? 'auto' : '100%',
                              marginBottom: index >= filteredUsers.length - 2 ? '4px' : '0',
                              marginTop: index >= filteredUsers.length - 2 ? '0' : '4px'
                            }}
                          >
                            <button
                              onClick={() => handleResendInvite(user)}
                              disabled={actionLoading === user.id}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              {actionLoading === user.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                              Resend Invite
                            </button>
                            <button
                              onClick={() => {
                                navigate(`/platform/tenants/${user.tenant_id}/users`);
                                setShowActionsFor(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Building2 className="w-4 h-4" />
                              View Tenant Users
                            </button>
                            <hr className="my-1 border-gray-100" />
                            {user.is_active !== false ? (
                              <button
                                onClick={() => handleSuspend(user)}
                                disabled={actionLoading === user.id}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                              >
                                {actionLoading === user.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserX className="w-4 h-4" />
                                )}
                                Suspend User
                              </button>
                            ) : (
                              <button
                                onClick={() => handleActivate(user)}
                                disabled={actionLoading === user.id}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
                              >
                                {actionLoading === user.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserCheck className="w-4 h-4" />
                                )}
                                Activate User
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-500">
                Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} users
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Click outside to close actions menu */}
      {showActionsFor && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowActionsFor(null)}
        />
      )}
    </div>
    </TooltipProvider>
  );
};

export default SystemUsersPage;
